import { useAuthMenu } from '@/services/authService/authMenu';
import { hasButtonPermission } from '@/utils/menuUtils';

/**
 * Page-level button realms from admin_menu.realm (menuType=3).
 * export is independent of read; do not derive canExport from canRead.
 */
export interface PageCrudRealms {
    /** Query / detail / list realms */
    read?: string | string[];
    /** Create / add / import-write realms */
    create?: string;
    /** Update / save / reorder realms */
    update?: string;
    /** Delete / remove / unbind realms */
    remove?: string;
    /** Export / template / download realms; never implied by read */
    export?: string | string[];
}

/** CRUD + independent export flags for one page. */
export interface PageCrudFlags {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canRemove: boolean;
    canExport: boolean;
    /** Read without CUD; does not imply export */
    viewOnly: boolean;
}

/**
 * True when the current user's menu tree contains the button realm.
 *
 * @param realm catalog `admin_menu.realm`; empty string is always false
 */
export function useButtonPerm(realm: string | undefined): boolean {
    const { data: menuTree = [] } = useAuthMenu();
    if (!realm) {
        return false;
    }
    return hasButtonPermission(menuTree, realm);
}

/**
 * Match any of the given realms against the current menu tree.
 *
 * @param menuTree current-user menu tree (includes menuType=3)
 * @param realms one realm or a list
 */
function matchAny(menuTree: Parameters<typeof hasButtonPermission>[0], realms: string | string[] | undefined): boolean {
    if (!realms) {
        return false;
    }
    const list = Array.isArray(realms) ? realms : [realms];
    return list.some((r) => !!r && hasButtonPermission(menuTree, r));
}

/**
 * Gate list/detail CUD and export buttons from the current-user menu tree.
 * Sidebar still uses type 1/2 only; this hook reads type 3 on the same tree.
 *
 * @param realms catalog realms for this page
 */
export function usePageCrud(realms: PageCrudRealms): PageCrudFlags {
    const { data: menuTree = [] } = useAuthMenu();
    const canCreate = !!realms.create && hasButtonPermission(menuTree, realms.create);
    const canUpdate = !!realms.update && hasButtonPermission(menuTree, realms.update);
    const canRemove = !!realms.remove && hasButtonPermission(menuTree, realms.remove);
    const canRead = realms.read === undefined ? true : matchAny(menuTree, realms.read);
    const canExport = matchAny(menuTree, realms.export);
    return {
        canRead,
        canCreate,
        canUpdate,
        canRemove,
        canExport,
        viewOnly: canRead && !canCreate && !canUpdate && !canRemove,
    };
}
